import { Alert, Stack, TextField, Typography } from '@mui/material'
import ContentDialog from '../../../components/Dialogs/ContentDialog'
import { useContext, useState } from 'react'
import Validations from '../../../core/functions/validations'
import LoadingBackdrop from '../../../components/Loading/LoadingBackdrop'
import ErrorSnackbar from '../../../components/Snackbar/ErrorSnackbar'
import SuccessSnackbar from '../../../components/Snackbar/SuccessSnackbar'
import UserAccountPermissionController from '../../../core/controllers/UserAccountPermissionController'
import { AuthContext } from '../../../core/context/AuthContext'

interface SettingsCollaboratorRegisterProps {
  open: boolean
  onClose: () => void
  onRefresh?: () => void
}

const SettingsCollaboratorRegister: React.FC<
  SettingsCollaboratorRegisterProps
> = ({ open, onClose, onRefresh }) => {
  const { getTenantId } = useContext(AuthContext)
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<{ email?: string }>({})
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const clearErrors = () => {
    setErrors({})
  }

  const handleClose = () => {
    clearErrors()
    setEmail('')
    onClose()
  }

  const handleChangeEmail = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEmail(e.target.value)
    clearErrors()
  }

  const handleChangeSaveCollaborator = async () => {
    if (email === '') {
      setErrors({ email: 'Campo obrigatório' })
      return
    }

    if (!Validations.email(email)) {
      setErrors({ email: 'E-mail inválido' })
      return
    }

    setSending(true)
    try {
      const response = await UserAccountPermissionController.createCollaborator(
        {
          tenantOwnerId: getTenantId(),
          tenantAuthorizedEmail: email,
        }
      )
      if (!response.success) {
        setError(response.error)
        return
      }

      setSuccess(true)
      onRefresh?.()
    } finally {
      setSending(false)
    }
  }

  const handleSuccessClose = () => {
    setSuccess(false)
    handleClose()
  }

  return (
    <>
      <ContentDialog
        open={open}
        onClose={handleClose}
        title="Novo colaborador"
        buttons={[
          {
            title: 'Agora não',
            type: 'negative',
            onClick: handleClose,
          },
          {
            title: 'Adicionar',
            type: 'positive',
            onClick: handleChangeSaveCollaborator,
          },
        ]}
      >
        <Stack direction="column" spacing={2} width="100%" sx={{ p: 2 }}>
          <Typography>
            Informe o e-mail do colaborador que receberá permissões de acesso à
            sua conta.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            size="small"
            type="text"
            label="E-mail do colaborador"
            value={email}
            onChange={handleChangeEmail}
            error={errors.email !== undefined}
            InputLabelProps={{
              shrink: email !== '' ? true : undefined,
            }}
            helperText={errors.email}
          />
          <Alert
            variant="standard"
            severity="warning"
            sx={{ width: '100%', mb: 2 }}
          >
            Atenção: apenas usuários já cadastrados na plataforma da Celetus
            podem ser adicionados como colaboradores.
          </Alert>
        </Stack>

        <LoadingBackdrop open={sending} />
      </ContentDialog>

      <ErrorSnackbar open={error !== ''} onClose={() => setError('')}>
        {error}
      </ErrorSnackbar>

      <SuccessSnackbar open={success} onClose={handleSuccessClose}>
        Adicionado com sucesso
      </SuccessSnackbar>
    </>
  )
}

export default SettingsCollaboratorRegister
